import { Input } from '@salesforce/design-system-react';
import { useEffect, useState } from 'react';

import { FieldLabelRow } from './FieldLabelRow';

interface FormulaFieldProps {
  description?: string;
  disabled?: boolean;
  error?: string;
  /** Value a cleared input snaps back to — every field has its own sensible one (e.g. 1 for a
   * per-increment divisor, 0 for most amounts), so it's always supplied by the caller.
   */
  fallback: number;
  label: string;
  onChange: (value: number) => void;
  tooltip?: string;
  value: number;
}

/**
 * A labeled numeric `Input` with optional field-level help and inline hint text below it —
 * `inlineHelpText` already puts that hint under the input (not between the label and input, the
 * way a plain description would read), so sibling fields' inputs stay aligned regardless of hint
 * length. `type="number"` keeps the numeric keyboard/semantics on mobile; the native spin-button
 * chrome that type normally shows is suppressed by `.qfw-grid input[type='number']` in
 * qualitySpecsWorkbench.css so every amount/bound field reads as a plain SLDS text field instead
 * of a generic browser counter control.
 *
 * Its visible label + help icon are rendered via `FieldLabelRow` rather than `Input`'s own
 * `label`/`fieldLevelHelpTooltip` props — see that component's own doc comment for why.
 *
 * The input's own text (`draft`) is tracked separately from the numeric `value` it reports
 * upward, so the field can sit empty mid-edit instead of being forced back to a number (and
 * therefore back to `fallback`) on every keystroke — a purely `value`-controlled input can never
 * actually be cleared, since the moment it goes blank the parent's numeric state (still the old
 * number, or `fallback`) gets reflected straight back into it. `draft` only resyncs from an
 * external `value` change (e.g. switching specs), not from this component's own `onChange`
 * round-trip, which always reports back the same number already sitting in `draft`. A blank or
 * otherwise non-numeric `draft` snaps to `fallback` on blur, matching the field's prior
 * always-numeric contract once the user is done editing.
 */
export const FormulaField: React.FC<FormulaFieldProps> = ({
  description,
  disabled,
  error,
  fallback,
  label,
  onChange,
  tooltip,
  value,
}) => {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
    // Resync only on external `value` changes, not local edits.
  }, [value]);

  return (
    <div>
      <FieldLabelRow label={label} tooltip={tooltip} />
      <Input
        assistiveText={{ label }}
        disabled={disabled}
        errorText={error}
        inlineHelpText={description}
        onBlur={() => {
          if (Number.isNaN(Number(draft)) || draft === '') {
            setDraft(String(fallback));
            onChange(fallback);
          }
        }}
        onChange={(_event, data) => {
          setDraft(data.value);
          if (data.value !== '' && !Number.isNaN(Number(data.value))) {
            onChange(Number(data.value));
          }
        }}
        type="number"
        value={draft}
      />
    </div>
  );
};
